import React, { useState, useEffect, useRef } from "react";
import {
  API_BASE,
  RAZORPAY_KEY_ID,
  EVENT_TIER_CONFIGS,
  PITCH_TIER_CONFIGS,
  AUDIENCE_TIER_CONFIGS, // NEW — add this export in RegisterModalShared.js (see note at bottom of file)
  FIELD_CONFIGS,
  INITIAL,
  Icon,
  HeroBackdrop,
  HeroMetaPills,
  MetaCardLight,
  Divider,
  modalStyles,
  useRazorpayScript,
  TierCard,
} from "./RegisterModalShared";

/* ============================================================
   TICKET REGISTRATION FLOW — shared by Event, Pitch & Audience
   ============================================================ */
function TicketRegistrationFlow({
  tiers,
  registrationType,
  tierHeroVariant,
  tierColumns,
  headerBadge,
  headerTitlePlain,
  headerTitleAccent,
  tierSubtitle,
  formSubtitle,
  eventTimeLabel,   // e.g. "16:00 – 21:00" — differs between flows
  heroMetaExtra,
  successTitle,
  successSubtitle,
  step,             // "tier" | "form" — now controlled by the parent, synced with history
  onStepChange,     // (nextStep) => void — call for FORWARD navigation only
  onBack,           // () => void — call for ALL back navigation (history.back())
  onClose,
  scrollContainerRef,
}) {
  const isPitch = registrationType === "pitch";
  const [selectedTier, setSelectedTier] = useState(null);
  const [form, setForm]       = useState(INITIAL);
  const [pitchIdea, setPitchIdea] = useState("");
  // Pitch deck upload state — { status: "idle"|"uploading"|"done"|"error", name, path, error }
  const [pitchDeck, setPitchDeck] = useState({ status: "idle", name: "", path: "", error: "" });
  const pitchDeckInputRef = useRef(null);
  // College ID card upload state — same shape as pitchDeck. Only required for the Student Pitch Pass.
  const [idCard, setIdCard] = useState({ status: "idle", name: "", path: "", error: "" });
  const idCardInputRef = useRef(null);
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [apiError, setApiError] = useState("");
  const razorpayReady = useRazorpayScript();
  const rzpInstanceRef = useRef(null);
  useEffect(() => {
    if (scrollContainerRef?.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [step, success, scrollContainerRef]);

  const tier = tiers.find(t => t.key === selectedTier);
  // Only the Student Pitch Pass requires a college ID card
  const isStudentTier = isPitch && selectedTier === "student";

  const heroPillItems = [
    { icon: "calendar", text: "19 December 2026" },
    { icon: "clock", text: eventTimeLabel },
    { icon: "pin", text: "TBD (Kolkata)" },
    ...(heroMetaExtra ? [heroMetaExtra] : []),
  ];

  const validate = () => {
    const errs = {};
    if (!form.full_name.trim())  errs.full_name    = "Please enter your name.";
    if (!form.email.trim())      errs.email        = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Enter a valid email address.";
    if (!form.phone.trim())      errs.phone        = "Please enter your phone number.";
    else if (form.phone.replace(/\D/g, "").length < 10)       errs.phone = "Enter a valid phone number.";
    if (!form.city.trim())       errs.city         = "Please enter your city.";
    if (isPitch && !pitchIdea.trim()) errs.pitch_idea = "Please tell us about your pitch idea.";
    if (isPitch && !pitchDeck.path)   errs.pitch_deck = "Please upload your pitch deck (PDF).";
    if (isStudentTier && !idCard.path) errs.id_card = "Please upload your college ID card.";
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    if (apiError) setApiError("");
  };

  const handlePitchIdeaChange = (e) => {
    setPitchIdea(e.target.value);
    if (errors.pitch_idea) setErrors(prev => ({ ...prev, pitch_idea: "" }));
    if (apiError) setApiError("");
  };

  // Pitch deck — uploads immediately on file select, before payment
  const handlePitchDeckSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (errors.pitch_deck) setErrors(prev => ({ ...prev, pitch_deck: "" }));
    if (apiError) setApiError("");

    if (file.type !== "application/pdf") {
      setPitchDeck({ status: "error", name: file.name, path: "", error: "Only PDF files are allowed." });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setPitchDeck({ status: "error", name: file.name, path: "", error: "File too large — max 10MB." });
      return;
    }

    setPitchDeck({ status: "uploading", name: file.name, path: "", error: "" });

    try {
      const formData = new FormData();
      formData.append("pitch_deck", file);
      const res = await fetch(`${API_BASE}/upload-pitch-deck.php`, { method: "POST", body: formData });
      const data = await res.json();
      if (data.success) {
        setPitchDeck({ status: "done", name: file.name, path: data.file_path, error: "" });
      } else {
        setPitchDeck({ status: "error", name: file.name, path: "", error: data.error || "Upload failed. Please try again." });
      }
    } catch {
      setPitchDeck({ status: "error", name: file.name, path: "", error: "Could not connect. Please try again." });
    }
  };

  const handleRemovePitchDeck = () => {
    setPitchDeck({ status: "idle", name: "", path: "", error: "" });
    if (pitchDeckInputRef.current) pitchDeckInputRef.current.value = "";
  };

  // College ID card — uploads immediately on file select, before payment (Student Pitch Pass only)
  const handleIdCardSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (errors.id_card) setErrors(prev => ({ ...prev, id_card: "" }));
    if (apiError) setApiError("");

    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      setIdCard({ status: "error", name: file.name, path: "", error: "Only JPG, PNG, or PDF files are allowed." });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setIdCard({ status: "error", name: file.name, path: "", error: "File too large — max 5MB." });
      return;
    }

    setIdCard({ status: "uploading", name: file.name, path: "", error: "" });

    try {
      const formData = new FormData();
      formData.append("id_card", file);
      const res = await fetch(`${API_BASE}/upload-id-card.php`, { method: "POST", body: formData });
      const data = await res.json();
      if (data.success) {
        setIdCard({ status: "done", name: file.name, path: data.file_path, error: "" });
      } else {
        setIdCard({ status: "error", name: file.name, path: "", error: data.error || "Upload failed. Please try again." });
      }
    } catch {
      setIdCard({ status: "error", name: file.name, path: "", error: "Could not connect. Please try again." });
    }
  };

  const handleRemoveIdCard = () => {
    setIdCard({ status: "idle", name: "", path: "", error: "" });
    if (idCardInputRef.current) idCardInputRef.current.value = "";
  };

  const handleSelectTier = (key) => { setSelectedTier(key); setApiError(""); };
  const handleContinueToForm = () => { if (selectedTier) onStepChange("form"); };

  const createOrder = async () => {
    const res = await fetch(`${API_BASE}/create-order.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ registration_type: registrationType, tier: selectedTier, ...form }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Could not start payment.");
    return data;
  };

  const verifyPayment = async (rzpResponse) => {
    const res = await fetch(`${API_BASE}/verify-payment.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        razorpay_payment_id: rzpResponse.razorpay_payment_id,
        razorpay_order_id:   rzpResponse.razorpay_order_id,
        razorpay_signature:  rzpResponse.razorpay_signature,
        registration_type: registrationType,
        tier: selectedTier,
        ...form,
        ...(isPitch ? { pitch_idea: pitchIdea, pitch_deck_path: pitchDeck.path } : {}),
        ...(isStudentTier ? { id_card_path: idCard.path } : {}),
      }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Payment verification failed.");
    return data;
  };

  const openCheckout = (orderData) => {
    const options = {
      key: RAZORPAY_KEY_ID,
      amount: orderData.amount,
      currency: orderData.currency || "INR",
      name: "Freedom Conclave 2026",
      description: `${tier.label} Registration`,
      order_id: orderData.order_id,
      prefill: { name: form.full_name, email: form.email, contact: form.phone },
      notes: { city: form.city, organization: form.organization, designation: form.designation },
      theme: { color: tier?.accent || "#f37c23" },
      handler: async (response) => {
        try {
          const verified = await verifyPayment(response);
          setSuccess({ ticket_id: verified.ticket_id, tier: selectedTier, amount: orderData.amount, pending: !!verified.pending });
        } catch (err) {
          setApiError(err.message || "We couldn't confirm your payment. If money was deducted, contact support with your payment ID — it won't be lost.");
        } finally {
          setLoading(false);
        }
      },
      modal: { ondismiss: () => setLoading(false) },
    };
    const rzp = new window.Razorpay(options);
    rzpInstanceRef.current = rzp;
    rzp.on("payment.failed", (response) => {
      setApiError(response?.error?.description || "Payment failed. Please try again.");
      setLoading(false);
    });
    rzp.open();
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    if (isPitch && pitchDeck.status === "uploading") {
      setApiError("Please wait for the pitch deck to finish uploading.");
      return;
    }
    if (isStudentTier && idCard.status === "uploading") {
      setApiError("Please wait for the ID card to finish uploading.");
      return;
    }
    if (!razorpayReady) { setApiError("Payment is still loading — please wait a moment and try again."); return; }
    setLoading(true);
    setApiError("");
    try {
      const orderData = await createOrder();
      openCheckout(orderData);
    } catch (err) {
      setLoading(false);
      setApiError(err.message || "Could not connect. Please check your internet and try again.");
    }
  };

  const renderSingleField = (field) => {
    if (field.isPhone) {
      return (
        <div className="rm-field" key={field.key}>
          <label className="rm-label">{field.label}{field.required && <span className="req">*</span>}</label>
          <div className={`rm-phone-group${errors[field.key] ? " error" : ""}`}>
            <span className="rm-phone-code">+91</span>
            <div className="rm-phone-input-wrap">
              <Icon name="phone" size={14} className="rm-input-icon" />
              <input
                type={field.type}
                name={field.key}
                value={form[field.key]}
                onChange={handleChange}
                placeholder={field.placeholder}
                autoComplete="off"
              />
            </div>
          </div>
          {errors[field.key] && <span className="rm-error-msg">{errors[field.key]}</span>}
        </div>
      );
    }
    return (
      <div className="rm-field" key={field.key}>
        <label className="rm-label">{field.label}{field.required && <span className="req">*</span>}</label>
        <div className="rm-input-wrap">
          <Icon name={field.icon} size={15} className="rm-input-icon" />
          <input
            className={`rm-input${errors[field.key] ? " error" : ""}`}
            type={field.type}
            name={field.key}
            value={form[field.key]}
            onChange={handleChange}
            placeholder={field.placeholder}
            autoComplete="off"
          />
        </div>
        {errors[field.key] && <span className="rm-error-msg">{errors[field.key]}</span>}
      </div>
    );
  };

  const renderFields = () => {
    const elements = [];
    let i = 0;
    while (i < FIELD_CONFIGS.length) {
      const f = FIELD_CONFIGS[i];
      if (f.half && FIELD_CONFIGS[i + 1]?.half) {
        const g = FIELD_CONFIGS[i + 1];
        elements.push(
          <div className="rm-field-row" key={f.key + g.key}>
            {[f, g].map(field => renderSingleField(field))}
          </div>
        );
        i += 2;
      } else {
        elements.push(renderSingleField(f));
        i++;
      }
    }
    return elements;
  };

  const renderPitchIdeaField = () => (
    <div className="rm-field" key="pitch_idea">
      <label className="rm-label">Your Pitch Idea<span className="req">*</span></label>
      <div className="rm-input-wrap rm-textarea-wrap">
        <textarea
          className={`rm-input rm-textarea${errors.pitch_idea ? " error" : ""}`}
          name="pitch_idea"
          value={pitchIdea}
          onChange={handlePitchIdeaChange}
          placeholder="In a few sentences, tell us what you're building and why it matters."
          rows={4}
        />
      </div>
      {errors.pitch_idea && <span className="rm-error-msg">{errors.pitch_idea}</span>}
      <span className="rm-field-hint">Our team reviews every pitch — this is what they'll read.</span>
    </div>
  );

  const renderPitchDeckField = () => (
    <div className="rm-field" key="pitch_deck">
      <label className="rm-label">Pitch Deck (PDF)<span className="req">*</span></label>
      <div className="rm-upload-field">
        <input
          ref={pitchDeckInputRef}
          type="file"
          accept="application/pdf"
          onChange={handlePitchDeckSelect}
          style={{ display: "none" }}
          id="rm-pitch-deck-input"
        />
        <label
          htmlFor={pitchDeck.status === "done" ? undefined : "rm-pitch-deck-input"}
          className={`rm-upload-box${(pitchDeck.status === "error" || errors.pitch_deck) ? " error" : ""}${pitchDeck.status === "done" ? " done" : ""}`}
        >
          <span className="rm-upload-icon">
            {pitchDeck.status === "uploading"
              ? <div className="rm-upload-spinner" />
              : <Icon name={pitchDeck.status === "done" ? "check" : "filePdf"} size={18} />
            }
          </span>
          <span className="rm-upload-text">
            {pitchDeck.status === "idle" && <><strong>Click to upload your pitch deck</strong><span>PDF only, max 10MB</span></>}
            {pitchDeck.status === "uploading" && <><strong>Uploading…</strong><span>{pitchDeck.name}</span></>}
            {pitchDeck.status === "done" && <><strong>Uploaded</strong><span>{pitchDeck.name}</span></>}
            {pitchDeck.status === "error" && <><strong style={{ color: "#e53e3e" }}>Upload failed</strong><span>{pitchDeck.error}</span></>}
          </span>
          {pitchDeck.status === "done" && (
            <button
              type="button"
              className="rm-upload-remove"
              onClick={(e) => { e.preventDefault(); handleRemovePitchDeck(); }}
              aria-label="Remove file"
            >
              <Icon name="x" size={14} />
            </button>
          )}
        </label>
        {errors.pitch_deck && <span className="rm-error-msg">{errors.pitch_deck}</span>}
      </div>
    </div>
  );

  // Only rendered for the Student Pitch Pass
  const renderIdCardField = () => (
    <div className="rm-field" key="id_card">
      <label className="rm-label">College ID Card<span className="req">*</span></label>
      <div className="rm-upload-field">
        <input
          ref={idCardInputRef}
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          onChange={handleIdCardSelect}
          style={{ display: "none" }}
          id="rm-id-card-input"
        />
        <label
          htmlFor={idCard.status === "done" ? undefined : "rm-id-card-input"}
          className={`rm-upload-box${(idCard.status === "error" || errors.id_card) ? " error" : ""}${idCard.status === "done" ? " done" : ""}`}
        >
          <span className="rm-upload-icon">
            {idCard.status === "uploading"
              ? <div className="rm-upload-spinner" />
              : <Icon name={idCard.status === "done" ? "check" : "idcard"} size={18} />
            }
          </span>
          <span className="rm-upload-text">
            {idCard.status === "idle" && <><strong>Click to upload your college ID card</strong><span>JPG, PNG, or PDF, max 5MB</span></>}
            {idCard.status === "uploading" && <><strong>Uploading…</strong><span>{idCard.name}</span></>}
            {idCard.status === "done" && <><strong>Uploaded</strong><span>{idCard.name}</span></>}
            {idCard.status === "error" && <><strong style={{ color: "#e53e3e" }}>Upload failed</strong><span>{idCard.error}</span></>}
          </span>
          {idCard.status === "done" && (
            <button
              type="button"
              className="rm-upload-remove"
              onClick={(e) => { e.preventDefault(); handleRemoveIdCard(); }}
              aria-label="Remove file"
            >
              <Icon name="x" size={14} />
            </button>
          )}
        </label>
        {errors.id_card && <span className="rm-error-msg">{errors.id_card}</span>}
        <span className="rm-field-hint">Used to verify your student status — please make sure the name and college are clearly visible.</span>
      </div>
    </div>
  );

  if (success) {
    const pending = !!success.pending;
    return (
      <>
        <div className="rm-header-plain">
          <div className="rm-header">
            <button className="rm-close" onClick={onClose} aria-label="Close"><Icon name="x" size={15} /></button>
            <div className="rm-header-badge">
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#f37c23", flexShrink: 0 }} />
              FREEDOM CONCLAVE 2026
            </div>
            <h2>{pending ? <>Application <span>submitted!</span></> : <>You're <span>registered!</span></>}</h2>
            <p>{pending ? "Our team is reviewing pitch applications now." : "Check your inbox — your QR ticket is on its way."}</p>
          </div>
        </div>
        <div className="rm-success">
          <div className="rm-success-icon"><Icon name={pending ? "clock" : "check"} size={40} /></div>
          <h3>{pending ? "Your Pitch Is Under Review" : successTitle}</h3>
          {pending ? (
            <p>We've received your payment and application. If your pitch is selected, we'll email your QR ticket and slot details to<br/><strong>{form.email}</strong>.</p>
          ) : (
            <p>{successSubtitle} <strong>{form.email}</strong>.<br/>Show it at the entrance on event day.</p>
          )}
          <div className="ticket-id">{success.ticket_id}</div>
          <div>
            <span className="paid-pill">
              <Icon name={pending ? "clock" : "check"} size={13} />
              {tiers.find(t => t.key === success.tier)?.label} · ₹{((success.amount || 0) / 100).toLocaleString("en-IN")} paid
            </span>
          </div>
          <p style={{ marginTop: 18, fontSize: 13 }}>
            <strong style={{ color: "#0d1433" }}>19 December 2026</strong> · TBD (Kolkata)
          </p>
          <button className="rm-submit" onClick={onClose}>
            Done
            <Icon name="arrowRight" size={16} style={{ color: "#fff" }} />
          </button>
        </div>
      </>
    );
  }

  if (step === "tier") {
    return (
      <>
        <div className="rm-hero-wrap rm-header-no-badge rm-anchor-bottom">
          <HeroBackdrop variant={tierHeroVariant} />
          <div className="rm-header">
            <button className="rm-back" onClick={onBack} aria-label="Back to registration options">
              <Icon name="chevronLeft" size={14} />Back
            </button>
            <button className="rm-close" onClick={onClose} aria-label="Close"><Icon name="x" size={15} /></button>
            {headerBadge && (
              <div className="rm-header-badge rm-with-back">
                <Icon name={headerBadge.icon} size={13} />
                {headerBadge.text}
              </div>
            )}
            <h2 style={!headerBadge ? { marginTop: 34 } : undefined}>{headerTitlePlain}<span>{headerTitleAccent}</span></h2>
            <p>{tierSubtitle}</p>
            <HeroMetaPills items={heroPillItems} />
          </div>
        </div>
        <div className="rm-body">
          <Divider>CHOOSE YOUR PASS</Divider>
          <div className={`rm-tier-list${tierColumns === 2 ? " rm-tier-grid-2" : ""}`} role="radiogroup">
            {tiers.map((t) => (
              <TierCard key={t.key} tier={t} selected={selectedTier === t.key} onSelect={handleSelectTier} />
            ))}
          </div>
        </div>
        {selectedTier ? (
          <div className="rm-tier-footer-dark">
            <div className="rm-tier-footer-dark-inner">
              <div className="rm-tfd-left">
                <span className="rm-tfd-icon"><Icon name="ticket" size={18} /></span>
                <div>
                  <span>Selected Pass</span>
                  <strong>{tier?.label}</strong>
                </div>
              </div>
              <div className="rm-tfd-amount">
                <span>Amount</span>
                <strong>₹{tier?.price.toLocaleString("en-IN")}</strong>
              </div>
              <div className="rm-tfd-right">
                <button className="rm-continue-btn" onClick={handleContinueToForm}>
                  Continue
                  <Icon name="arrowRight" size={15} style={{ color: "#fff" }} />
                </button>
                <div className="rm-tfd-secure">
                  <Icon name="lock" size={13} />
                  <div>
                    <strong>Secure Checkout</strong><br/>Powered by Razorpay
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="rm-tier-footer">
            <div className="rm-tier-footer-inner">
              <div className="rm-tier-footer-note">
                <Icon name="shield" size={18} />
                <div>
                  <p>All prices are inclusive of taxes.</p>
                  <p>You'll pay securely via Razorpay on the next step.</p>
                </div>
              </div>
              <button className="rm-continue-btn" disabled>
                Continue
                <Icon name="arrowRight" size={15} style={{ color: "#fff" }} />
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  // step === "form"
  return (
    <>
      <div className="rm-hero-wrap rm-header-no-badge rm-anchor-bottom">
        <HeroBackdrop variant="form" />
        <div className="rm-header">
          <button className="rm-back" onClick={onBack} aria-label="Back to pass selection">
            <Icon name="chevronLeft" size={14} />Back
          </button>
          <button className="rm-close" onClick={onClose} aria-label="Close"><Icon name="x" size={15} /></button>
          <h2 style={{ marginTop: 34 }}>{headerTitlePlain}<span>{headerTitleAccent}</span></h2>
          <p>{formSubtitle}</p>
          <HeroMetaPills items={heroPillItems.slice(0, 3)} />
        </div>
      </div>
      <div className="rm-body">
        {tier && (
          <div className="rm-selected-card" style={{ background: `linear-gradient(90deg, ${tier.accent}14, #fff 65%)`, border: `1.5px solid ${tier.accent}33` }}>
            <div className="rm-selected-card-left">
              <div className="rm-selected-card-icon">
                <Icon name={tier.icon} size={22} style={{ color: tier.accent }} />
              </div>
              <div>
                <strong>{tier.label}</strong>
                <div className="sc-desc">{tier.description || tier.tagline}</div>
              </div>
            </div>
            <div className="rm-selected-card-right">
              <span className="sc-price" style={{ color: tier.accent }}>₹{tier.price.toLocaleString("en-IN")}</span>
              <button className="rm-change-btn" onClick={onBack}>Change Pass</button>
            </div>
          </div>
        )}

        <div className="rm-details-header">
          <div className="rm-details-header-icon"><Icon name="user" size={20} /></div>
          <div>
            <h3>Your Details</h3>
            <p>Please enter accurate information to receive your QR ticket.</p>
          </div>
        </div>
        <div className="rm-details-hr" />

        {renderFields()}
        {isPitch && renderPitchIdeaField()}
        {isPitch && renderPitchDeckField()}
        {isStudentTier && renderIdCardField()}
        {apiError && (
          <div style={{ background: "#fff0f0", border: "1.5px solid #fca5a5", borderRadius: 10, padding: "12px 16px", marginTop: 12, fontSize: 13, color: "#c00", fontWeight: 600 }}>
            {apiError}
          </div>
        )}

        <div className="rm-pay-card">
          <div className="rm-pay-card-note">
            <Icon name="shield" size={22} />
            <p>
              {isPitch
                ? <>Secure &amp; Hassle-free<br/>Your payment is secured by Razorpay. Selected applicants receive their QR ticket by email.</>
                : <>Secure &amp; Hassle-free<br/>Your payment is secured by Razorpay. You'll get your QR ticket instantly.</>
              }
            </p>
          </div>
          <button className="rm-submit" onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <><div className="rm-spinner" /> {rzpInstanceRef.current ? "Waiting for payment…" : "Preparing payment…"}</>
            ) : (
              <>
                <Icon name="shield" size={16} style={{ color: "#fff" }} />
                {isPitch
                  ? <>Pay ₹{tier?.price.toLocaleString("en-IN")} &amp; Submit Application</>
                  : <>Pay ₹{tier?.price.toLocaleString("en-IN")} &amp; Get My QR Ticket</>
                }
                <Icon name="arrowRight" size={16} style={{ color: "#fff" }} />
              </>
            )}
          </button>
        </div>
        <div className="rm-trust-row">
          <span className="rm-trust-item"><Icon name="lock" size={13} />Secure Payments</span>
          <span className="rm-trust-item">
            <Icon name={isPitch ? "shield" : "bolt"} size={13} />
            {isPitch ? "Reviewed Applications" : "Instant Confirmation"}
          </span>
          <span className="rm-trust-item"><Icon name="shield" size={13} />100% Safe</span>
        </div>
        <div className="rm-fine">
          <Icon name="shield" size={13} />
          {isPitch
            ? "All prices are inclusive of taxes. Applications are reviewed before a pitch slot is confirmed."
            : "All prices are inclusive of taxes. You'll pay securely via Razorpay on the next step."
          }
        </div>
      </div>
    </>
  );
}

/* ============================================================
   CHOICE SCREEN — Event Registration vs Startup Pitching vs Audience
   ============================================================ */
const CHOICE_META = [
  { icon: "calendar", line1: "19 December 2026", line2: "Monday" },
  { icon: "clock", line1: "TBD", line2: "IST" },
  { icon: "pin", line1: "TBD (Kolkata)", line2: "India" },
];

function ChoiceScreen({ onChoose, onClose }) {
  return (
    <>
      <div className="rm-hero-wrap">
        <HeroBackdrop variant="choice" particles />
        <div className="rm-header">
          <button className="rm-close" onClick={onClose} aria-label="Close"><Icon name="x" size={15} /></button>
          <div className="rm-header-badge">
            <Icon name="star" size={12} />
            EAST INDIA'S BIGGEST BUSINESS CONCLAVE
          </div>
          <h2>Register for <span>Freedom Conclave</span></h2>
          <p>Be a part of East India's most impactful business gathering where ideas meet opportunities and leaders connect.</p>
          <div className="rm-hero-features">
            <div className="rm-hero-feature">
              <span className="rm-hero-feature-icon"><Icon name="lightbulb" size={18} gradient /></span>
              <div><strong>Inspire</strong><small>New Ideas</small></div>
            </div>
            <span className="rm-hero-feature-divider" />
            <div className="rm-hero-feature">
              <span className="rm-hero-feature-icon"><Icon name="network" size={18} gradient /></span>
              <div><strong>Network</strong><small>With Leaders</small></div>
            </div>
            <span className="rm-hero-feature-divider" />
            <div className="rm-hero-feature">
              <span className="rm-hero-feature-icon"><Icon name="handshake" size={18} gradient /></span>
              <div><strong>Collaborate</strong><small>For Growth</small></div>
            </div>
            <span className="rm-hero-feature-divider" />
            <div className="rm-hero-feature">
              <span className="rm-hero-feature-icon"><Icon name="megaphone" size={18} gradient /></span>
              <div><strong>Influence</strong><small>The Future</small></div>
            </div>
          </div>
        </div>
      </div>

      <MetaCardLight items={CHOICE_META} />

      <div className="rm-body">
        <Divider>CHOOSE HOW YOU WANT TO PARTICIPATE</Divider>
        <div className="rm-choice-list rm-choice-list-3" style={{ display: "flex", justifyContent: "center" }}>
          <div
            className="rm-choice-card"
            style={{
              "--card-border": "rgba(243,124,35,0.4)",
              "--card-border-hover": "rgba(243,124,35,0.65)",
              "--card-dot": "rgba(243,124,35,0.22)",
              "--card-accent": "#f37c23",
            }}
            onClick={() => onChoose("event")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onChoose("event"); }}
          >
            <span className="rm-choice-tag" style={{ background: "#fdebd6", color: "#f37c23" }}>Join the Conclave</span>
            <div className="rm-choice-top">
              <div className="rm-choice-icon-wrap">
                <div className="rm-choice-icon" style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.95), #fdebd6)" }}>
                  <Icon name="ticket" size={24} style={{ color: "#f37c23" }} />
                </div>
              </div>
              <div className="rm-choice-main">
                <h3>Event Registration</h3>
                <p>General, Business, VIP passes — or showcase your brand with an exhibition booth.</p>
              </div>
            </div>
            <div className="rm-choice-divider" style={{ background: "rgba(243,124,35,0.3)" }} />
            <div className="rm-choice-feats-row">
              <div className="rm-choice-feats">
                <div className="rm-choice-feat"><Icon name="clock" size={18} /><span>Access to All Sessions</span></div>
                <div className="rm-choice-feat"><Icon name="megaphone" size={18} /><span>Networking Opportunities</span></div>
                <div className="rm-choice-feat"><Icon name="medal" size={18} /><span>Certificate of Participation</span></div>
                <div className="rm-choice-feat"><Icon name="gift" size={18} /><span>Goodies and More</span></div>
              </div>
              <button className="rm-choice-arrow-btn" style={{ background: "#f37c23" }} aria-label="Register for the event">
                <Icon name="arrowRight" size={18} style={{ color: "#fff" }} />
              </button>
            </div>
          </div>

          {/* <div
            className="rm-choice-card"
            style={{
              "--card-border": "rgba(56,102,224,0.4)",
              "--card-border-hover": "rgba(56,102,224,0.65)",
              "--card-dot": "rgba(56,102,224,0.22)",
              "--card-accent": "#3866e0",
            }}
            onClick={() => onChoose("pitch")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onChoose("pitch"); }}
          > */}
            {/* <span className="rm-choice-tag" style={{ background: "#e2e7f7", color: "#3866e0" }}>Pitch Your Startup</span> */}
            {/* <div className="rm-choice-top">
              <div className="rm-choice-icon-wrap">
                <div className="rm-choice-icon" style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.95), #e2e7f7)" }}>
                  <Icon name="rocket" size={24} style={{ color: "#3866e0" }} />
                </div>
              </div> */}
              {/* <div className="rm-choice-main">
                <h3>Startup Pitching Registration</h3>
                <p>Pitch your startup to our investor panel — Student or General pass. 100 slots, first come, first served.</p>
              </div>
            </div>
            <div className="rm-choice-divider" style={{ background: "rgba(56,102,224,0.3)" }} />
            <div className="rm-choice-feats-row">
              <div className="rm-choice-feats">
                <div className="rm-choice-feat"><Icon name="shield" size={18} /><span>Pitch to Investors</span></div>
                <div className="rm-choice-feat"><Icon name="star" size={18} /><span>Visibility &amp; Recognition</span></div>
                <div className="rm-choice-feat"><Icon name="lightbulb" size={18} /><span>Valuable Mentorship</span></div>
                <div className="rm-choice-feat"><Icon name="trendUp" size={18} /><span>Grow Your Network</span></div>
              </div>
              <button className="rm-choice-arrow-btn" style={{ background: "#3866e0" }} aria-label="Register to pitch">
                <Icon name="arrowRight" size={18} style={{ color: "#fff" }} />
              </button>
            </div>
          </div>

          <div
            className="rm-choice-card"
            style={{
              "--card-border": "rgba(34,168,89,0.4)",
              "--card-border-hover": "rgba(34,168,89,0.65)",
              "--card-dot": "rgba(34,168,89,0.22)",
              "--card-accent": "#22a859",
            }}
            onClick={() => onChoose("audience")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onChoose("audience"); }}
          >
            <span className="rm-choice-tag" style={{ background: "#dcf5e6", color: "#22a859" }}>Attend &amp; Support</span>
            <div className="rm-choice-top">
              <div className="rm-choice-icon-wrap">
                <div className="rm-choice-icon" style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.95), #dcf5e6)" }}>
                  <Icon name="users" size={24} style={{ color: "#22a859" }} />
                </div>
              </div> */}
              {/* <div className="rm-choice-main">
                <h3>Audience Registration</h3>
                <p>Join as an audience member and witness innovative startups pitch to investors.</p>
              </div> */}
            {/* </div>
            <div className="rm-choice-divider" style={{ background: "rgba(34,168,89,0.3)" }} />
            <div className="rm-choice-feats-row">
              <div className="rm-choice-feats">
                <div className="rm-choice-feat"><Icon name="users" size={18} /><span>Watch Live Pitches</span></div>
                <div className="rm-choice-feat"><Icon name="megaphone" size={18} /><span>Learn &amp; Get Inspired</span></div>
                <div className="rm-choice-feat"><Icon name="handshake" size={18} /><span>Connect with Founders</span></div>
                <div className="rm-choice-feat"><Icon name="star" size={18} /><span>Be Part of the Ecosystem</span></div>
              </div> */}
              {/* <button className="rm-choice-arrow-btn" style={{ background: "#22a859" }} aria-label="Register as audience">
                <Icon name="arrowRight" size={18} style={{ color: "#fff" }} />
              </button> */}
            {/* </div> */}
          {/* </div> */}
        </div>

        <div className="rm-stats-bar">
          <div className="rm-stats-items">
            <div className="rm-stat">
              <span className="rm-stat-icon"><Icon name="users" size={20} gradient /></span>
              <div><strong>350+</strong><span>Entrepreneurs &amp;<br/>Business Leaders</span></div>
            </div>
            <div className="rm-stat">
              <span className="rm-stat-icon"><Icon name="megaphone" size={20} gradient /></span>
              <div><strong>20+</strong><span>Eminent Speakers<br/>&amp; Panelists</span></div>
            </div>
            <div className="rm-stat">
              <span className="rm-stat-icon"><Icon name="handshake" size={20} gradient /></span>
              <div><strong>Powerful</strong><span>Networking<br/>Opportunities</span></div>
            </div>
            <div className="rm-stat">
              <span className="rm-stat-icon"><Icon name="star" size={20} gradient /></span>
              <div><strong>One Day.</strong><span>Limitless<br/>Possibilities.</span></div>
            </div>
          </div>
          <button className="rm-stats-cta" onClick={() => onChoose("event")}>
            <strong>Reserve Your Spot Today!</strong>
            <span>Let's build the future of business, together.</span>
          </button>
        </div>
      </div>
    </>
  );
}

/* ============================================================
   ROOT COMPONENT
   ============================================================ */

// Maps each flow's URL to the modal screen it should open on. Used both
// for the initial mount and to keep the screen in sync every time the URL
// changes for ANY reason — Back, Forward, or a programmatic navigate() call.
const PATH_TO_FLOW = {
  "/freedom-conclave/register": "choice",
  "/freedom-conclave/register-event": "event-tier",
  "/freedom-conclave/register-event/details": "event-form",
  "/freedom-conclave/register-pitching": "pitch-tier",
  "/freedom-conclave/register-pitching/details": "pitch-form",
  "/freedom-conclave/register-pitching-audience": "audience-tier",
  "/freedom-conclave/register-pitching-audience/details": "audience-form",
};

const FLOW_URLS = {
  "event-tier": "/freedom-conclave/register-event",
  "event-form": "/freedom-conclave/register-event/details",
  "pitch-tier": "/freedom-conclave/register-pitching",
  "pitch-form": "/freedom-conclave/register-pitching/details",
  "audience-tier": "/freedom-conclave/register-pitching-audience",
  "audience-form": "/freedom-conclave/register-pitching-audience/details",
};
const getUrlForScreen = (s) => {
  if (s === "choice") return "/freedom-conclave/register";
  return FLOW_URLS[s] || "/freedom-conclave/register";
};

export default function RegisterModal({ isOpen, onClose, navigate: routerNavigate, pathname }) {
  const [screen, setScreen] = useState(PATH_TO_FLOW[pathname] || "choice");
  const overlayRef = useRef(null);

  // Single source of truth: whenever the URL changes — Back click, Forward
  // click, or our own navigate() below — sync the screen to match it.
  // No separate history bookkeeping, so nothing can desync from the URL.
  useEffect(() => {
    setScreen(PATH_TO_FLOW[pathname] || "choice");
  }, [pathname]);

  useEffect(() => {
    if (overlayRef.current) {
      overlayRef.current.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [screen]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!isOpen) return null;

  // Forward navigation (choosing a flow, tier → form): always goes through
  // React Router so its location and this modal's screen stay in lockstep.
  const navigate = (nextScreen) => {
    routerNavigate(getUrlForScreen(nextScreen));
  };

  // Back navigation also goes through React Router — this is what fixes
  // the "URL changes but content doesn't" bug, since it's the same system
  // that's driving the `pathname` prop above.
  const goBack = () => {
    routerNavigate(-1);
  };

  const handleClose = () => {
    onClose();
  };

  const flow = screen === "choice" ? "choice" : screen.split("-")[0];
  const step = screen.includes("-") ? screen.split("-")[1] : null;

  return (
    <div className="rm-overlay" ref={overlayRef}>
      <style>{modalStyles}</style>
      <svg width="0" height="0" style={{ position: "absolute" }}>
        <defs>
          <linearGradient id="rm-gold-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f37c23" />
            <stop offset="100%" stopColor="#ffd93d" />
          </linearGradient>
        </defs>
      </svg>
      <img className="rm-bg-watermark left" src="/Event/left.avif" alt="" />
      <img className="rm-bg-watermark right" src="/Event/right.avif" alt="" />
      <div className="rm-modal" role="dialog" aria-modal="true" aria-label="Register for Freedom Conclave 2026">
        {flow === "choice" && (
          <ChoiceScreen onChoose={(f) => navigate(`${f}-tier`)} onClose={handleClose} />
        )}
        {flow === "event" && (
          <TicketRegistrationFlow
            key="event"
            scrollContainerRef={overlayRef}
            tiers={EVENT_TIER_CONFIGS}
            registrationType="event"
            tierHeroVariant="eventTier"
            tierColumns={1}
            headerBadge={null}
            headerTitlePlain="Register for "
            headerTitleAccent="Freedom Conclave 2026"
            tierSubtitle="Choose your pass to get started"
            formSubtitle="Fill in your details — your unique QR ticket will be emailed to you instantly after payment."
            eventTimeLabel="TBD"
            successTitle="Registration Confirmed!"
            successSubtitle="We've sent your entry QR code to"
            step={step}
            onStepChange={(s) => navigate(`event-${s}`)}
            onBack={goBack}
            onClose={handleClose}
          />
        )}
        {flow === "pitch" && (
          <TicketRegistrationFlow
            key="pitch"
            scrollContainerRef={overlayRef}
            tiers={PITCH_TIER_CONFIGS}
            registrationType="pitch"
            tierHeroVariant="pitchTier"
            tierColumns={1}
            headerBadge={{ icon: "rocket", text: "STARTUP PITCHING SESSION" }}
            headerTitlePlain="Startup Pitching "
            headerTitleAccent="Registration"
            tierSubtitle="Choose your pitch pass to get started and showcase your startup idea to investors and industry leaders."
            formSubtitle="Fill in your details and your pitch idea — we'll review applications and email your QR ticket to selected pitchers."
            eventTimeLabel="10:00 – 15:00"
            heroMetaExtra={null}
            successTitle="Pitch Slot Confirmed!"
            successSubtitle="We've sent your entry QR code and pitch slot details to"
            step={step}
            onStepChange={(s) => navigate(`pitch-${s}`)}
            onBack={goBack}
            onClose={handleClose}
          />
        )}
        {flow === "audience" && (
          <TicketRegistrationFlow
            key="audience"
            scrollContainerRef={overlayRef}
            tiers={AUDIENCE_TIER_CONFIGS}
            registrationType="audience"
            tierHeroVariant="audienceTier"
            tierColumns={1}
            headerBadge={{ icon: "users", text: "AUDIENCE REGISTRATION" }}
            headerTitlePlain="Audience "
            headerTitleAccent="Registration"
            tierSubtitle="Choose your pass to watch the startup pitches and connect with founders."
            formSubtitle="Fill in your details — your unique QR ticket will be emailed to you instantly after payment."
            eventTimeLabel="10:00 – 15:00"
            heroMetaExtra={null}
            successTitle="Registration Confirmed!"
            successSubtitle="We've sent your entry QR code to"
            step={step}
            onStepChange={(s) => navigate(`audience-${s}`)}
            onBack={goBack}
            onClose={handleClose}
          />
        )}
      </div>
    </div>
  );
}